/**
 * Speaker profiles -- group 06 (high-sensitivity horns, full-range and coaxial
 * designs, plus one ultra-steep-slope multiway as the counterexample).
 *
 * Sensitivity spans 89 to 107 dB across this group, which is a factor of about
 * 250 in amplifier power for the same SPL. That single spread is what makes the
 * group useful: at 107 dB the noise floor of everything upstream becomes part of
 * the sound and a 2 W amplifier is enough, while at 89 dB the upstream noise is
 * inaudible and the amplifier is doing real work. Crossover philosophy spans the
 * same range, from a single capacitor to roughly 50 dB per octave.
 *
 * The kit derives the anechoic baseline from the published bass alignment and HF
 * limit; `responseTilt` is the authored voicing and is the part that carries the
 * character of each design. Impedance is modelled from the alignment so that
 * low-damping-factor amplifiers interact with it correctly.
 */

import type { SpeakerProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeSpeaker, usd } from '../kit';

export const SPEAKERS_06: SpeakerProfile[] = [
  makeSpeaker({
    mfr: 'Gauder Akustik',
    model: 'Berlina RC7',
    price: usd(
      70000,
      'APPROXIMATE',
      2023,
      'Approximately USD 70,000 per pair. Exact figure not verified.',
    ),
    year: 2013,
    country: 'Germany',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['diamond-dome', 'dynamic'],
    driverCount: 5,
    wayCount: 3,
    sensitivityDb: 89,
    nominalOhms: 4,
    minOhms: 3.4,
    bass: [28, 23],
    hfExtension: 45000,
    portTuningHz: 27,
    crossovers: [[180, 50], [2600, 50]],
    responseTilt: [[20, 0], [45, 0.4], [120, -0.3], [400, 0.1], [1200, 0], [2600, -0.2], [5000, 0.5], [10000, 0.4], [20000, -0.8]],
    resonances: [[260, -46, 24], [610, -50, 28]],
    coverageAngleDeg: 90,
    recommendedWatts: [60, 500],
    dimensionsMm: [1310, 300, 520],
    massKg: 92,
    thdPercent: 0.2,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#8e9bb5',
    tags: ['steep-crossover', 'ceramic-diaphragm', 'diamond-tweeter', 'german'],
    notes:
      'The defining Gauder choice is crossover slope: the acoustic transitions run near 50 dB per octave rather than the usual 12 to 24, so each driver works over a narrow band and no driver is ever asked to radiate near its own breakup. The cost, which this model carries, is phase: a filter that steep rotates phase through several full turns at each transition, so step response is poor even though on-axis magnitude is unusually flat and off-axis behaviour is well controlled through the crossover region. Ceramic and diamond diaphragms push first breakup far above the passband, which is why modelled distortion is the lowest in this group and the cabinet resonances are high-Q and very low in level rather than broad and shallow.',
    provenanceNote:
      'The steep crossover slopes and the ceramic and diamond driver complement are from Gauder Akustik published material. Sensitivity, nominal and minimum impedance, bass extension, port tuning, dimensions and mass are ESTIMATED. The reflex alignment in particular is an INFERENCE from the cabinet rather than a quoted specification, and the voicing curve is authored, not measured.',
  }),

  makeSpeaker({
    mfr: 'Avantgarde Acoustic',
    model: 'Duo GT',
    price: usd(
      85000,
      'APPROXIMATE',
      2023,
      'Approximately USD 85,000 per pair. Exact figure not verified.',
    ),
    year: 2019,
    country: 'Germany',
    speakerType: 'horn',
    enclosure: 'horn-loaded',
    drivers: ['compression-driver', 'dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 107,
    nominalOhms: 18,
    minOhms: 14,
    bass: [18, 16],
    hfExtension: 22000,
    crossovers: [[170, 12], [1900, 12]],
    responseTilt: [[20, 0.6], [60, -0.4], [170, 0.8], [500, -0.5], [1200, 1.2], [1900, -0.9], [4000, 0.7], [9000, -0.6], [16000, -1.8]],
    resonances: [[210, -34, 11], [780, -38, 15]],
    coverageAngleDeg: 70,
    recommendedWatts: [2, 100],
    dimensionsMm: [1580, 700, 800],
    massKg: 130,
    thdPercent: 0.35,
    harmonicShape: HARMONIC_SHAPES.horn,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#d4622f',
    tags: ['spherical-horn', 'high-sensitivity', 'powered-bass', 'set-friendly'],
    notes:
      'Large spherical horns carry the midrange and treble, with everything below about 170 Hz handled by powered sub-horns; only the passive horn section is presented to the external amplifier in this model, which is why the load is a benign 18 ohms nominal and the sensitivity is 107 dB. At that sensitivity the noise floor of every upstream component becomes part of the audible result and a 2 W amplifier reaches realistic levels, so this profile is the one that exposes upstream noise rather than upstream power limits. The authored voicing carries the two artefacts a spherical horn genuinely has: a mouth-reflection ripple near horn cutoff and a presence-region rise where the horn loading peaks before the pattern starts to narrow.',
    provenanceNote:
      'Sensitivity, the spherical horn geometry, the powered bass section and the crossover points are from Avantgarde published material. The 18 ohm nominal impedance follows standard Avantgarde horn loading and is treated here as ESTIMATED. The powered bass is excluded from the electrical load model. Response shape, cabinet resonances and distortion are MODELLED.',
  }),

  makeSpeaker({
    mfr: 'Cessaro Horn Acoustics',
    model: 'Liszt',
    price: usd(
      120000,
      'APPROXIMATE',
      2023,
      'Approximately USD 120,000 per pair. Cessaro builds to order and pricing is not published; exact figure not verified.',
    ),
    year: 2012,
    country: 'Germany',
    speakerType: 'horn',
    enclosure: 'horn-loaded',
    drivers: ['compression-driver', 'dynamic'],
    driverCount: 6,
    wayCount: 4,
    sensitivityDb: 106,
    nominalOhms: 16,
    minOhms: 12,
    bass: [30, 25],
    hfExtension: 25000,
    crossovers: [
      { freqHz: 250, slopeDbOct: 12, alignment: 'bessel', offsetMm: 220 },
      { freqHz: 1300, slopeDbOct: 12, alignment: 'bessel', offsetMm: 140 },
      { freqHz: 7000, slopeDbOct: 12, alignment: 'bessel', offsetMm: 60 },
    ],
    responseTilt: [[20, -0.5], [70, 0.9], [250, -1.1], [600, 0.5], [1300, -0.7], [3000, 1.1], [7000, -0.9], [12000, 0.4], [20000, -1.5]],
    resonances: [[130, -32, 8], [420, -36, 12]],
    coverageAngleDeg: 45,
    recommendedWatts: [3, 60],
    dimensionsMm: [1800, 900, 1100],
    massKg: 260,
    thdPercent: 0.4,
    harmonicShape: HARMONIC_SHAPES.horn,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#7a4b2a',
    tags: ['all-horn', 'four-way', 'multicell', 'ultra-high-end'],
    notes:
      'A four-way in which every band including the bass is horn-loaded, so each diaphragm sees a high acoustic impedance across its whole range and cone excursion stays very small; the modelled excursion-limited distortion knee therefore sits far below any direct radiator in this group, and power compression is minimal. The price is size and pattern control: coverage narrows to roughly 45 degrees, which cuts sidewall reflection energy sharply and makes the in-room result strongly dependent on toe-in and listening distance. Four horn segments also mean four acoustic origins, and the profile carries explicit driver offsets so the arrival-time errors through each crossover are part of the model rather than ignored.',
    provenanceNote:
      'Cessaro publishes almost no numerical specification. The all-horn four-way architecture and the compression-driver complement are from the manufacturer product description. Sensitivity, impedance, crossover frequencies, driver offsets, bass extension, dimensions and mass are ESTIMATED or MODELLED; the 12 dB per octave Bessel sections in particular are a modelling assumption, not a published figure.',
  }),

  makeSpeaker({
    mfr: 'Living Voice',
    model: 'Avatar OBX-RW',
    price: usd(
      16000,
      'APPROXIMATE',
      2023,
      'Approximately USD 16,000 per pair including the outboard crossover boxes. Exact figure not verified.',
    ),
    year: 2009,
    country: 'United Kingdom',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 94,
    nominalOhms: 6,
    minOhms: 5,
    bass: [32, 26],
    hfExtension: 24000,
    portTuningHz: 31,
    crossovers: [
      { freqHz: 400, slopeDbOct: 6, alignment: 'first-order' },
      { freqHz: 4000, slopeDbOct: 6, alignment: 'first-order' },
    ],
    responseTilt: [[20, 0], [50, 0.3], [150, 0.5], [400, 0.2], [900, -0.3], [2000, -0.7], [4000, -0.4], [8000, 0.4], [16000, -1.0]],
    resonances: [[165, -27, 7], [390, -31, 10]],
    coverageAngleDeg: 110,
    recommendedWatts: [8, 120],
    dimensionsMm: [965, 240, 280],
    massKg: 26,
    thdPercent: 0.35,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#a37f5c',
    tags: ['outboard-crossover', 'shallow-slopes', 'valve-friendly', 'narrow-baffle'],
    notes:
      'A narrow reflex column with the crossover moved out of the cabinet into separate boxes, voiced for low-power valve amplification: 94 dB sensitivity into a 6 ohm load that does not dip hard means a 20 W amplifier never leaves its linear region. The shallow first-order sections keep the drivers summing in phase over a wide band, at the cost of large overlap regions where two drivers radiate the same frequencies from physically separated points, so the model shows wider vertical lobing than the steep-slope entry in this group and a listening axis that matters more. The voicing carries a small lower-midrange lift and a slightly recessed upper midrange, the balance this lineage of British designs comes from.',
    provenanceNote:
      'Sensitivity, nominal impedance and the outboard crossover construction are from Living Voice published material. Crossover frequencies and slopes, bass extension, port tuning, cabinet resonances, dimensions and mass are ESTIMATED. The voicing curve is authored, not measured.',
  }),

  makeSpeaker({
    mfr: 'Zu Audio',
    model: 'Druid Mk6',
    price: usd(
      13500,
      'APPROXIMATE',
      2023,
      'Approximately USD 13,500 per pair. Exact figure not verified.',
    ),
    year: 2016,
    country: 'United States',
    speakerType: 'floorstander',
    enclosure: 'transmission-line',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 101,
    nominalOhms: 16,
    minOhms: 12,
    bass: [35, 28],
    hfExtension: 30000,
    crossovers: [{ freqHz: 12000, slopeDbOct: 6, alignment: 'first-order' }],
    responseTilt: [[20, 0], [45, -0.5], [100, 0.6], [300, 0.3], [900, -0.4], [2500, 1.3], [5000, 0.6], [9000, -1.4], [16000, 0.8]],
    resonances: [[155, -24, 6], [370, -29, 9]],
    coverageAngleDeg: 70,
    recommendedWatts: [2, 100],
    dimensionsMm: [1245, 305, 305],
    massKg: 27,
    thdPercent: 0.6,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#b5483f',
    tags: ['full-range', 'crossoverless', 'supertweeter', 'quarter-wave'],
    notes:
      'A 260 mm full-range paper driver runs from the bass to about 12 kHz with no crossover component anywhere in its path; the only filter in the loudspeaker is a single series capacitor feeding the supertweeter. That removes crossover phase rotation from the entire vocal band, which is the whole point of the design, but it also means the driver is used well into its own breakup region, so the authored voicing carries the characteristic rise around 2 to 3 kHz and the dip above it, and modelled distortion is the highest in this group. The cabinet is a controlled-leakage quarter-wave path rather than a tuned reflex, so the low-frequency rolloff is shallower and better damped than a ported alignment reaching the same f3, and group delay near cutoff is correspondingly lower.',
    provenanceNote:
      'The crossover-less full-range driver, the single-capacitor supertweeter high-pass, the controlled-leakage cabinet loading, the 16 ohm nominal impedance and a sensitivity figure near 101 dB are from Zu published material. They are tagged ESTIMATED here because the measurement conditions behind the sensitivity figure are not stated. Bass extension, cabinet resonances, dimensions, mass and the voicing curve are ESTIMATED or MODELLED.',
  }),

  makeSpeaker({
    mfr: 'DeVore Fidelity',
    model: 'Orangutan O/96',
    price: usd(
      22000,
      'APPROXIMATE',
      2023,
      'Approximately USD 22,000 per pair at current pricing; the model launched materially lower. Exact figure not verified.',
    ),
    year: 2013,
    country: 'United States',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 96,
    nominalOhms: 10,
    minOhms: 8,
    bass: [30, 25],
    hfExtension: 26000,
    portTuningHz: 29,
    crossovers: [[2200, 12]],
    responseTilt: [[20, 0], [50, 0.4], [110, 0.8], [300, 0.3], [800, -0.2], [2200, -0.6], [5000, -0.5], [9000, 0.2], [16000, -1.2]],
    resonances: [[190, -28, 8], [430, -33, 11]],
    coverageAngleDeg: 120,
    recommendedWatts: [10, 150],
    dimensionsMm: [915, 470, 305],
    massKg: 41,
    thdPercent: 0.4,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#9a7b4f',
    tags: ['wide-baffle', 'paper-cone', 'silk-dome', 'two-way'],
    notes:
      'A 250 mm paper-cone woofer and a soft-dome tweeter in a wide, shallow plywood cabinet, crossed once at about 2.2 kHz. The wide baffle is the acoustically significant decision: the baffle-step transition falls near 250 Hz, computed here from the 470 mm cabinet width rather than assumed, so the change from half-space to full-space radiation happens below the vocal range instead of running through it, which is why the voicing needs only a small lower-midrange lift instead of a broad correction. A 10 ohm nominal load that does not fall below about 8 ohms, combined with 96 dB sensitivity, keeps a low-feedback valve amplifier inside its linear region across the whole range.',
    provenanceNote:
      'Sensitivity, nominal impedance, driver sizes and cabinet dimensions are from DeVore Fidelity published material. Bass extension, port tuning, crossover frequency, minimum impedance, cabinet resonances, mass and the voicing curve are ESTIMATED or MODELLED.',
  }),

  makeSpeaker({
    mfr: 'Volti Audio',
    model: 'Rival',
    price: usd(
      15000,
      'APPROXIMATE',
      2023,
      'Approximately USD 15,000 per pair depending on finish and options. Exact figure not verified.',
    ),
    year: 2016,
    country: 'United States',
    speakerType: 'horn',
    enclosure: 'ported',
    drivers: ['compression-driver', 'dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 98,
    nominalOhms: 8,
    minOhms: 6,
    bass: [35, 29],
    hfExtension: 20000,
    portTuningHz: 34,
    crossovers: [[600, 12], [5000, 12]],
    responseTilt: [[20, 0], [60, 0.7], [150, -0.5], [400, -0.8], [900, 1.0], [2000, 0.4], [5000, -1.1], [9000, 0.6], [16000, -2.0]],
    resonances: [[210, -30, 10], [640, -34, 13]],
    coverageAngleDeg: 80,
    recommendedWatts: [5, 150],
    dimensionsMm: [1140, 495, 400],
    massKg: 54,
    thdPercent: 0.45,
    harmonicShape: HARMONIC_SHAPES.horn,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#8f6b3a',
    tags: ['hybrid-loading', 'wooden-horn', 'reflex-bass', 'american'],
    notes:
      'Hybrid loading: a 380 mm woofer in a reflex cabinet below about 600 Hz, then a wooden midrange horn and a horn tweeter above it. Because the bass is a direct radiator rather than horn-loaded, the model uses a reflex impedance curve with its two peaks and a saddle at tuning instead of the single broad peak the fully horn-loaded entries in this group show, while the harmonic structure above 600 Hz follows the horn law with distortion rising against SPL. The audible consequence in the model is a directivity discontinuity at the 600 Hz handover, where a wide-radiating cone gives way to a horn covering roughly 80 degrees, which changes the direct-to-reverberant ratio across the lower midrange.',
    provenanceNote:
      'Sensitivity, the three-way hybrid horn architecture and the 15 inch woofer are from Volti published material. Crossover frequencies and slopes, nominal and minimum impedance, bass extension, port tuning, cabinet resonances, dimensions, mass and the voicing curve are ESTIMATED or MODELLED.',
  }),

  makeSpeaker({
    mfr: 'Tannoy',
    model: 'Westminster Royal GR',
    price: usd(
      45000,
      'APPROXIMATE',
      2023,
      'Approximately USD 45,000 per pair. Exact figure not verified.',
    ),
    year: 2015,
    country: 'United Kingdom',
    speakerType: 'coaxial',
    enclosure: 'horn-loaded',
    drivers: ['coaxial-point-source', 'compression-driver'],
    driverCount: 1,
    wayCount: 2,
    sensitivityDb: 99,
    nominalOhms: 8,
    minOhms: 6,
    bass: [18, 15],
    hfExtension: 27000,
    crossovers: [{ freqHz: 1100, slopeDbOct: 12, alignment: 'butterworth' }],
    responseTilt: [[20, 0.8], [60, 1.0], [150, -0.6], [400, -1.0], [900, 0.9], [1500, -1.2], [3000, 0.5], [7000, -0.4], [14000, 0.9], [20000, -1.4]],
    resonances: [[95, -26, 6], [280, -30, 9]],
    coverageAngleDeg: 75,
    recommendedWatts: [10, 300],
    dimensionsMm: [1397, 980, 560],
    massKg: 133,
    thdPercent: 0.4,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#6f5b3e',
    tags: ['dual-concentric', 'folded-bass-horn', 'point-source', 'corner-placement'],
    notes:
      'A 380 mm Dual Concentric in a front-loaded folded bass horn: the compression driver fires through a throat formed by the woofer cone itself, so both bands share one acoustic axis and the crossover near 1.1 kHz produces no vertical lobing error, which is the property no other entry in this group has. The cone-as-horn arrangement is also the source of the characteristic artefacts, and the voicing curve carries them explicitly rather than smoothing them away: a narrow suckout just above crossover where the cone profile stops behaving as a smooth horn wall, and a broad lower-midrange dip from the folded bass horn path length. The 18 Hz corner is real but depends entirely on corner loading, which is why room coupling is set high and the modelled in-room result changes more with placement than for any sealed design.',
    provenanceNote:
      'The Dual Concentric architecture, the 15 inch driver, the folded horn cabinet, dimensions and mass are from Tannoy published material. Sensitivity, impedance, the 1.1 kHz crossover point, bass extension and cabinet resonances are ESTIMATED. The voicing curve is authored to represent the known artefacts of a cone-throat compression driver and is not a measurement.',
  }),
];
